import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Chip, IconButton, TextField, InputAdornment, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  ToggleButton, ToggleButtonGroup, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, Stack, Avatar, Collapse, Alert,
} from '@mui/material';
import {
  Search, Inbox, Storefront, KeyboardArrowDown, KeyboardArrowUp,
  LocalMall, CurrencyRupee,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { studioIdOf } from '../utils/studioOf';
import { useAuth } from '../contexts/AuthContext';
import StudioSelector, { StudioOption } from './StudioSelector';
import { isOwnerRole } from '../utils/roles';

/**
 * The fulfilment board: PLACED → PACKED → READY_FOR_PICKUP → COLLECTED.
 *
 * Transitions are mirrored from the server's ALLOWED_TRANSITIONS
 * (merchandise-order-status.enum.ts). The server is authoritative and rejects
 * anything illegal — this map only decides which button to offer, so a studio
 * never sees an action that would 400.
 */
const NEXT_ACTIONS: Record<string, { status: string; label: string }[]> = {
  PENDING_PAYMENT: [],
  PLACED: [
    { status: 'PACKED', label: 'Mark packed' },
    { status: 'CANCELLED', label: 'Cancel' },
  ],
  PACKED: [
    { status: 'READY_FOR_PICKUP', label: 'Ready for pickup' },
    { status: 'CANCELLED', label: 'Cancel' },
  ],
  READY_FOR_PICKUP: [
    { status: 'COLLECTED', label: 'Mark collected' },
    { status: 'CANCELLED', label: 'Cancel' },
  ],
  COLLECTED: [],
  CANCELLED: [],
  REFUNDED: [],
  EXPIRED: [],
};

const STATUS_STYLE: Record<string, { label: string; color: any }> = {
  PENDING_PAYMENT: { label: 'Awaiting payment', color: 'default' },
  PLACED: { label: 'Placed', color: 'info' },
  PACKED: { label: 'Packed', color: 'primary' },
  READY_FOR_PICKUP: { label: 'Ready for pickup', color: 'warning' },
  COLLECTED: { label: 'Collected', color: 'success' },
  CANCELLED: { label: 'Cancelled', color: 'error' },
  REFUNDED: { label: 'Refunded', color: 'error' },
  EXPIRED: { label: 'Expired', color: 'default' },
};

const rupee = (n: number) => '₹' + (n ?? 0).toLocaleString('en-IN');

interface Props {
  selectedStudio: StudioOption | null;
  onStudioChange: (s: StudioOption | null) => void;
  onToast: (msg: string, severity: 'success' | 'error') => void;
}

export default function MerchandiseOrdersTab({ selectedStudio, onStudioChange, onToast }: Props) {
  const { admin } = useAuth();
  const isStudio = admin?.role !== 'super_admin' && admin?.role !== 'admin';
  // Refunds move real money and are studio-admin only on the server; hide the
  // button for anyone who would just get a 403.
  const canRefund = admin?.role === 'super_admin' || admin?.role === 'admin' || isOwnerRole(admin?.role);

  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('active');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [refundOrder, setRefundOrder] = useState<any | null>(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refunding, setRefunding] = useState(false);

  const studioId = selectedStudio?._id;

  const fetchOrders = useCallback(async () => {
    if (!isStudio && !studioId) { setOrders([]); return; }
    setLoading(true);
    try {
      const params: any = { pageSize: 100, ...(studioId ? { studioId } : {}) };
      if (status !== 'active' && status !== 'all') params.status = status;
      // 'active' = the default board (server already hides unpaid orders).
      if (status === 'all') params.includePending = 'true';
      const res: any = await apiService.getMerchandiseOrders(params);
      setOrders(res?.orders || []);
    } catch (err: any) {
      onToast(err?.response?.data?.message || 'Failed to load orders', 'error');
    } finally {
      setLoading(false);
    }
  }, [isStudio, studioId, status, onToast]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const advance = async (order: any, next: string) => {
    setBusyId(order._id);
    try {
      await apiService.updateMerchandiseOrderStatus(order._id, { status: next }, studioId, studioIdOf(order.studio));
      onToast(`${order.orderNumber} → ${STATUS_STYLE[next]?.label ?? next}`, 'success');
      fetchOrders();
    } catch (err: any) {
      onToast(err?.response?.data?.message || 'Could not update the order', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const submitRefund = async () => {
    if (!refundOrder) return;
    setRefunding(true);
    try {
      await apiService.refundMerchandiseOrder(
        refundOrder._id,
        {
          ...(refundAmount ? { amount: Number(refundAmount) } : {}),
          ...(refundReason ? { reason: refundReason } : {}),
        },
        studioId,
        studioIdOf(refundOrder.studio),
      );
      onToast(`Refunded ${refundOrder.orderNumber}`, 'success');
      setRefundOrder(null);
      setRefundAmount('');
      setRefundReason('');
      fetchOrders();
    } catch (err: any) {
      onToast(err?.response?.data?.message || 'Refund failed', 'error');
    } finally {
      setRefunding(false);
    }
  };

  const filtered = orders.filter(o => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      o.orderNumber?.toLowerCase().includes(q) ||
      o.pickupCode?.toLowerCase().includes(q) ||
      o.user?.name?.toLowerCase().includes(q) ||
      o.user?.phoneNumber?.includes(search)
    );
  });

  return (
    <Box>
      <Box display="flex" gap={2} mb={3} flexWrap="wrap" alignItems="center">
        <StudioSelector value={selectedStudio} onChange={onStudioChange} required />
        <TextField
          size="small" placeholder="Order no, pickup code, customer…"
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 280 }}
        />
        <ToggleButtonGroup size="small" exclusive value={status} onChange={(_, v) => v && setStatus(v)}>
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="PLACED">Placed</ToggleButton>
          <ToggleButton value="PACKED">Packed</ToggleButton>
          <ToggleButton value="READY_FOR_PICKUP">Ready</ToggleButton>
          <ToggleButton value="COLLECTED">Collected</ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : !isStudio && !studioId ? (
        <Box textAlign="center" py={10}>
          <Storefront sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography color="text.secondary">Select a studio to view its orders</Typography>
        </Box>
      ) : filtered.length === 0 ? (
        <Box textAlign="center" py={10}>
          <Inbox sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography color="text.secondary">
            {orders.length === 0 ? 'No orders yet' : 'No orders match your filters'}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell width={40} />
                {['Order', 'Customer', 'Items', 'Total', 'Payment', 'Status', 'Pickup', 'Placed', ''].map(h => (
                  <TableCell key={h}><Typography variant="caption" fontWeight={700}>{h}</Typography></TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(o => {
                const open = expanded === o._id;
                const actions = NEXT_ACTIONS[o.status] ?? [];
                const units = (o.items || []).reduce((s: number, i: any) => s + i.qty, 0);
                return (
                  <React.Fragment key={o._id}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton size="small" onClick={() => setExpanded(open ? null : o._id)}>
                          {open ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                        </IconButton>
                      </TableCell>
                      <TableCell><Typography variant="body2" fontWeight={700}>{o.orderNumber}</Typography></TableCell>
                      <TableCell>
                        <Typography variant="body2">{o.user?.name || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">{o.user?.phoneNumber}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip icon={<LocalMall sx={{ fontSize: 14 }} />} size="small" variant="outlined"
                          label={`${units} ${units === 1 ? 'unit' : 'units'}`} />
                      </TableCell>
                      <TableCell><Typography variant="body2" fontWeight={600}>{rupee(o.total)}</Typography></TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined"
                          color={o.modeOfPayment === 'cash' ? 'warning' : 'default'}
                          label={o.modeOfPayment === 'cash' ? 'At studio' : o.modeOfPayment === 'free' ? 'Free' : 'Online'} />
                      </TableCell>
                      <TableCell>
                        <Chip size="small" color={STATUS_STYLE[o.status]?.color ?? 'default'}
                          label={STATUS_STYLE[o.status]?.label ?? o.status} />
                      </TableCell>
                      <TableCell>
                        {/* Staff verify this at the counter before handing the bag over. */}
                        <Typography variant="body2" fontFamily="monospace" fontWeight={700}>{o.pickupCode}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(o.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          {busyId === o._id ? <CircularProgress size={16} /> : actions.map(a => (
                            <Button key={a.status} size="small"
                              variant={a.status === 'CANCELLED' ? 'text' : 'contained'}
                              color={a.status === 'CANCELLED' ? 'error' : 'primary'}
                              onClick={() => advance(o, a.status)}>
                              {a.label}
                            </Button>
                          ))}
                          {o.status === 'CANCELLED' && canRefund &&
                            o.modeOfPayment === 'online' && o.paymentId && (
                            <Button size="small" variant="outlined" color="error"
                              startIcon={<CurrencyRupee sx={{ fontSize: 14 }} />}
                              onClick={() => { setRefundOrder(o); setRefundAmount(String(o.total)); }}>
                              Refund
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell colSpan={10} sx={{ py: 0, border: open ? undefined : 'none' }}>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                          <Box py={2} px={1} display="flex" gap={4} flexWrap="wrap">
                            <Box flex={1} minWidth={280}>
                              <Typography variant="caption" fontWeight={700} color="text.secondary">ITEMS</Typography>
                              {(o.items || []).map((i: any, idx: number) => (
                                <Box key={idx} display="flex" alignItems="center" gap={1.5} mt={1}>
                                  <Avatar variant="rounded" src={i.imageSnapshot} sx={{ width: 32, height: 32 }} />
                                  <Box flex={1}>
                                    <Typography variant="body2">
                                      {i.titleSnapshot}
                                      {i.variantLabel && (
                                        <Typography component="span" variant="caption" color="text.secondary">
                                          {' '}· {i.variantLabel}
                                        </Typography>
                                      )}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {i.qty} × {rupee(i.priceSnapshot)} · GST {i.gstRateSnapshot}%
                                      {i.variantSku ? ` · ${i.variantSku}` : ''}
                                    </Typography>
                                  </Box>
                                  <Typography variant="body2" fontWeight={600}>{rupee(i.lineTotal)}</Typography>
                                </Box>
                              ))}
                              <Box mt={1.5} pt={1.5} borderTop="1px solid" borderColor="divider">
                                {[
                                  ['Subtotal', o.subtotal],
                                  ...(o.discount ? [['Discount', -o.discount]] : []),
                                  ['GST', o.taxes],
                                  ['Total', o.total],
                                ].map(([label, val]: any, i, arr) => (
                                  <Box key={label} display="flex" justifyContent="space-between">
                                    <Typography variant="caption" fontWeight={i === arr.length - 1 ? 700 : 400}>{label}</Typography>
                                    <Typography variant="caption" fontWeight={i === arr.length - 1 ? 700 : 400}>{rupee(val)}</Typography>
                                  </Box>
                                ))}
                              </Box>
                            </Box>

                            <Box flex={1} minWidth={260}>
                              <Typography variant="caption" fontWeight={700} color="text.secondary">HISTORY</Typography>
                              {(o.statusHistory || []).map((h: any, idx: number) => (
                                <Box key={idx} display="flex" justifyContent="space-between" mt={1}>
                                  <Box>
                                    <Typography variant="body2">{STATUS_STYLE[h.status]?.label ?? h.status}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      by {h.actor}{h.note ? ` — ${h.note}` : ''}
                                    </Typography>
                                  </Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {new Date(h.at).toLocaleString('en-IN', {
                                      day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
                                    })}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Refund */}
      <Dialog open={!!refundOrder} onClose={() => setRefundOrder(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Refund {refundOrder?.orderNumber}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This moves real money back through Razorpay and cannot be undone.
          </Alert>
          <TextField
            label="Amount (₹)" type="number" fullWidth size="small" sx={{ mb: 2 }}
            value={refundAmount} onChange={e => setRefundAmount(e.target.value)}
            inputProps={{ min: 0.01, max: refundOrder?.total }}
            helperText={`Full amount is ${rupee(refundOrder?.total ?? 0)}. Lower it for a partial refund.`}
          />
          <TextField
            label="Reason (optional)" fullWidth size="small" multiline rows={2}
            value={refundReason} onChange={e => setRefundReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundOrder(null)} disabled={refunding}>Cancel</Button>
          <Button color="error" variant="contained" onClick={submitRefund} disabled={refunding}>
            {refunding ? <CircularProgress size={18} /> : 'Refund'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
